import * as React from 'react';
import { FieldProps } from 'formik';
import { FormGroup, Input, Label } from 'reactstrap';

type FormikInputFieldProps = FieldProps & {
  label: string;
  description: string | null;
  dataType: string;
  sensitive: boolean;
  isEdit: boolean;
  options: SelectOptions[];
  checkboxLabel: string;
  paddingBottom: number;
  disabled: boolean;
  noAutoComplete: boolean;
};

export type SelectOptions = {
  value: string;
  display: string;
};

const FormikInputField = ({
  field,
  // { name, value, onChange, onBlur }
  form: { touched, errors, /* setTouched, */ setFieldValue },
  // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  label,
  description,
  dataType,
  sensitive,
  isEdit,
  options,
  paddingBottom = 0,
  disabled,
  noAutoComplete,
}: FormikInputFieldProps) => {
  const [textInputRef, setTextInputRef] =
    React.useState<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (field.value === '') {
      if (textInputRef !== null) {
        textInputRef.focus();
      }
    }
  }, [textInputRef, field.value]);
  let isError = null,
    widget = null;
  const fieldProps: { [key: string]: any } = { ...field };
  if (touched[field.name]) {
    isError = !!errors[field.name];
    if (isError) {
      fieldProps.invalid = true;
    } else {
      fieldProps.valid = true;
    }
  }

  fieldProps.disabled = disabled;

  // json file upload
  if (dataType === 'json') {
    if (fieldProps.value === null) {
      fieldProps.disabled = true;
    }
    delete fieldProps.value;
    fieldProps.onChange = (event: React.SyntheticEvent<HTMLInputElement>) =>
      setFieldValue(field.name, (event.currentTarget.files || [])[0]);

    fieldProps.className = 'my-2';
    fieldProps.type = 'file';
  } else if (sensitive) {
    fieldProps.type = 'password';
  } else if (dataType === 'select') {
    fieldProps.type = 'select';
    widget = (
      <Input {...fieldProps} style={{ paddingBottom: `${paddingBottom}px` }}>
        {options.map((option) => {
          return (
            <option key={option.value} value={option.value}>
              {option.display}
            </option>
          );
        })}
      </Input>
    );
  } else if (dataType === 'radio') {
    fieldProps.type = 'radio';
    widget = options.map((option) => {
      return (
        <Label
          className="pl-4"
          style={{ paddingBottom: `${paddingBottom}px` }}
          key={option.value}
          check
        >
          <Input
            checked={field.value === option.value}
            onChange={() => {
              setFieldValue(field.name, option.value);
            }}
            key={option.value}
            value={option.value}
            type="radio"
            name={fieldProps.name}
          />
          {option.display}
        </Label>
      );
    });
  } else if (dataType === 'checkbox') {
    fieldProps.type = 'checkbox';
    return (
      <FormGroup style={{ marginBottom: '0.5rem' }}>
        <Label for={field.name}>{label}</Label>
        <FormGroup check>
          <Label className="description">
            <Input
              {...fieldProps}
              checked={field.value === 'true'}
              onChange={() => {
                setFieldValue(
                  field.name,
                  field.value === 'true' ? 'false' : 'true',
                );
              }}
            />
            {description ? (
              <span className="description">{description}</span>
            ) : null}
          </Label>
        </FormGroup>
        <span style={{ fontSize: '10px', color: '#CE444B' }}>
          {isError && errors[field.name]}
          &nbsp;
        </span>
      </FormGroup>
    );
  } else if (dataType === 'number') {
    fieldProps.type = 'number';
  } else if (dataType === 'textarea') {
    fieldProps.type = 'textarea';
  }

  if (isEdit && (sensitive || dataType === 'json')) {
    // add reuse option to JSON and sensitive fields
    widget = (
      <FormGroup tag="fieldset" className="mb-0" data-cy={field.name}>
        <FormGroup check>
          <Label check>
            <Input
              type="radio"
              checked={field.value === null}
              onChange={() => {
                setFieldValue(field.name, null);
              }}
            />{' '}
            Use existing value
          </Label>
        </FormGroup>
        <FormGroup check>
          <Label check>
            <Input
              type="radio"
              style={{ height: '1.75rem' }}
              checked={field.value !== null}
              onChange={() => {
                setFieldValue(field.name, '');
              }}
            />{' '}
            {fieldProps.type === 'file' ? (
              <Input {...fieldProps} />
            ) : (
              <Input
                {...{
                  ...fieldProps,
                  disabled: fieldProps.value === null,
                  value: fieldProps.value || '',
                }}
                innerRef={(ref) => {
                  setTextInputRef(ref);
                }}
              />
            )}
          </Label>
        </FormGroup>
      </FormGroup>
    );
  } else if (!['select', 'radio', 'checkbox'].includes(dataType)) {
    widget = (
      <Input
        {...fieldProps}
        autoComplete={noAutoComplete ? 'off' : ''}
        data-lpignore={noAutoComplete ? 'true' : ''} // data-lpignore removes lastpass icons
      />
    );
  }

  return (
    <FormGroup style={{ marginBottom: '0.5rem' }}>
      <Label for={field.name}>{label}</Label>
      {description ? <span className="description">{description}</span> : null}
      {widget}
      <span style={{ fontSize: '10px', color: '#CE444B' }}>
        {isError && errors[field.name]}
        &nbsp;
      </span>
    </FormGroup>
  );
};

export default FormikInputField;
